import Cookies from "js-cookie";

import { config } from "@/config/config";
import { IAuthenticated } from "@/context";

interface CustomFetchOptions extends RequestInit {
  skipLoading?: boolean;
  skipAuth?: boolean;
}

type FetchResponse<T> = T;

export class CustomFetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function customFetch<T = unknown>(
  input: RequestInfo,
  init?: CustomFetchOptions
): Promise<FetchResponse<T>> {
  const { skipLoading = false, skipAuth = false, ...fetchOptions } = init || {};
  
  // Ler o cookie de forma segura
  let userData: IAuthenticated = { token: "", organization_root_id: "" };
  try {
    const authUserCookie = Cookies.get("authUser");
    if (authUserCookie) {
      userData = JSON.parse(authUserCookie);
    }
  } catch (error) {
    console.error("Erro ao ler cookie authUser:", error);
  }
  
  const loadingElement = !skipLoading
    ? document.getElementById("loading")
    : null;
  const baseUrl = config.baseUrl;

  try {
    loadingElement?.classList.remove("hidden");

    // Criar Headers a partir dos headers passados (se houver)
    const headers = new Headers();
    
    // Primeiro, adicionar headers passados na chamada da API
    if (fetchOptions?.headers) {
      if (fetchOptions.headers instanceof Headers) {
        fetchOptions.headers.forEach((value, key) => {
          headers.set(key, value);
        });
      } else if (typeof fetchOptions.headers === 'object') {
        Object.entries(fetchOptions.headers).forEach(([key, value]) => {
          if (value) {
            headers.set(key, String(value));
          }
        });
      }
    }

    // Se já tiver Content-Type nos headers passados, não sobrescrever
    if (fetchOptions?.body && !(fetchOptions.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    
    if (!skipAuth) {
      const token = userData?.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      } else {
        console.warn("[ AUTH ]: Block request with no authorization token", {
          url: input,
          hasUserData: !!userData,
          hasToken: !!token,
        });
      }
    }

    const language = localStorage.getItem("locale");
    headers.set("language", language === "pt-BR" ? "pt" : "en");
    
    // Escopo da organização: respeita header explícito (ex.: tela /clients/:clientId/...).
    // Só aplica organization_root_id do cookie quando o chamador não definiu x-organization-id.
    if (!headers.has("x-organization-id")) {
      if (userData?.organization_root_id) {
        headers.set("x-organization-id", userData.organization_root_id);
      } else if (!skipAuth) {
        console.warn("[ AUTH ]: Missing organization_root_id in userData", {
          url: input,
          hasUserData: !!userData,
        });
      }
    }

    if (!headers.has("x-brand")) {
      const brandName = (config.brandName || "").toLowerCase();
      const brandSlug = brandName.includes("infinity") ? "infinity" : "soulhub";
      headers.set("x-brand", brandSlug);
    }

    // Converter Headers para objeto simples para garantir compatibilidade
    const headersObject: Record<string, string> = {};
    headers.forEach((value, key) => {
      headersObject[key] = value;
    });
    
    // Garantir que o body e headers estão corretos
    const finalOptions: RequestInit = {
      method: fetchOptions?.method || "GET",
      headers: headersObject,
      body: fetchOptions?.body,
    };
    
    // Adicionar outros campos do fetchOptions se necessário (mas não sobrescrever body/headers)
    if (fetchOptions) {
      Object.keys(fetchOptions).forEach((key) => {
        if (key !== "body" && key !== "headers" && key !== "method") {
          (finalOptions as any)[key] = (fetchOptions as any)[key];
        }
      });
    }
    
    const response = await fetch(`${baseUrl}${input}`, finalOptions);

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("Unauthorized (401). Redirecting to login page.");
        handleUnauthorized();
      }

      let errorBody: any;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = {
          status: response.status,
          message: `HTTP error ${response.status}: ${response.statusText}`,
        };
      }

      const requestPath = typeof input === "string" ? input : String(input);
      const fullUrl = `${baseUrl}${requestPath}`;

      // Log completo do erro para debug (URL ajuda a identificar qual endpoint falhou)
      console.error("Erro da API:", {
        method: finalOptions.method ?? "GET",
        path: requestPath,
        fullUrl,
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      });

      // Tentar extrair mensagem de erro de diferentes formatos
      let errorMessage = `HTTP error ${response.status}: ${response.statusText}`;
      
      // Verificar diferentes formatos de resposta de erro
      if (errorBody.message) {
        errorMessage = errorBody.message;
      } else if (errorBody.error) {
        errorMessage = errorBody.error;
      } else if (errorBody.errors) {
        errorMessage = errorBody.errors;
      } else if (typeof errorBody === 'string') {
        errorMessage = errorBody;
      }
      
      // Se for um array de mensagens (validação), juntar todas
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage.join(", ");
      }
      
      // Se houver um campo 'error' ou 'errors' como objeto, tentar extrair mensagens
      if (errorBody.error && typeof errorBody.error === 'object' && !Array.isArray(errorBody.error)) {
        const errorMessages = Object.values(errorBody.error).flat();
        if (errorMessages.length > 0) {
          errorMessage = Array.isArray(errorMessages[0]) 
            ? errorMessages.flat().join(", ")
            : errorMessages.join(", ");
        }
      }

      throw new CustomFetchError(
        errorMessage,
        response.status
      );
    }

    const contentType = response.headers.get("Content-Type");
    if (contentType?.includes("application/json")) {
      return response.json() as Promise<T>;
    }

    return response.text() as unknown as T;
  } finally {
    loadingElement?.classList.add("hidden");
  }
}

function handleUnauthorized() {
  Cookies.remove("authUser", {
    path: "/",
    domain: config.environment === "local" ? "localhost" : ".soulhub.tec.br",
  });
  window.location.href = "/login";
}
